import React from 'react';

// Lib
import { useEvent } from 'lib/hooks';
import { sanitize } from 'dompurify';
import { convertToUniqueIdArray } from 'lib/helpers';
import { messageNotEmptyValue } from 'lib/thesaurus';
import { useAppDispatch } from 'lib/hooks/useDispatch';

// Actions
import { openModal } from 'reducers/modal';
import { showLoader } from 'reducers/loader';
import { showMessage } from 'reducers/messages';
import * as actionsCreator from 'pages/Search/reducer';

// Type
import { TFuncArgs } from 'pages/Search/type';
import { useCallback } from 'react';

type IUseSearchByListOfIDsHandlers = {
	goTo: number;
	number: number;
	typeData: string;
	totalPages: number;
	setIsShowLoader: any;
	value: string | undefined;
	resetObject: Record<string, any>;
}

export const useSearchByListOfIDsHandler = (props: IUseSearchByListOfIDsHandlers) => {
	const {
		value,
		goTo,
		typeData,
		number,
		totalPages,
		resetObject,
		setIsShowLoader,
	} = props;
	const dispatch = useAppDispatch();

	const onChangeInput = useEvent((value: string): void => {
		dispatch(actionsCreator.searchTextUpdateByIds(sanitize(value)));

		setIsShowLoader(false);
	});

	const pageHandlers: Record<string, (page: number) => any> = {
		compounds: actionsCreator.handlerChangePageCompoundByIds,
		drugs: actionsCreator.handlerChangePageDrugsByIds,
		impurities: actionsCreator.handlerChangePageImpuritiesByIds,
		chemicalProbes: actionsCreator.handlerChangePageChemicalProbesByIds,
	};

	const getRequestPagination = useCallback((numberPagination: number) => {
		if (typeData !== 'libraries' && numberPagination !== number) {
			dispatch(actionsCreator.handlerChangePriceCartCompound(resetObject));
		}

		const handler = pageHandlers[typeData];
		if (handler && numberPagination !== number) {
			dispatch(handler(numberPagination));
		}

		dispatch(actionsCreator.handleShowTextSearchListByIds(true));
		setIsShowLoader(true);
	}, [dispatch, typeData, number]);

	const onPagePress = useEvent((number: number) => getRequestPagination(number - 1));
	const onNextPressLast = useEvent(() => getRequestPagination((totalPages || 0) - 1));
	const onPrevPressLast = useEvent(() => getRequestPagination(0));

	const doSearch = useCallback((value: string) =>{
		if (!value?.length) {
			dispatch(showMessage(messageNotEmptyValue));
			return false;
		}

		dispatch(showLoader(true));
		dispatch(actionsCreator.handleShowTextSearchListByIds(true));
		dispatch(
			actionsCreator.searchTextByIds({
				value: value,
				isHistory: false,
				type: 'update-data',
			}),
		);
		setIsShowLoader(true);
	}, [value, dispatch]);

	const handlerSubmit = useEvent(() => {
		doSearch(value || '');
	});

	const leftIconAction = useEvent(() => {
		doSearch(value || '');
	});

	const downloadSDF = (sdfVersion: string) => {
		const ids = convertToUniqueIdArray(value || '');
		dispatch(actionsCreator.downloadSDF({ ids, sdfVersion }));
	};

	const handleDownloadConfirm = useEvent(() => {
		dispatch(
			openModal({
				textClose: 'NO',
				hiddenTitle: true,
				textSubmit: 'YES',
				type: 'downloadSDF',
				title: 'Select version',
				actionSubmit: downloadSDF,
			}),
		);
	});

	const handleClickType = useEvent((type: string) => {
		dispatch(actionsCreator.handlerChangeTypeDataListByIds(type));
	});

	const handlerSubmitGoTo = useEvent(() => {
		getRequestPagination(goTo - 1);
	});

	const onKeyPress = useEvent((e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			const inputElement = e.target as HTMLInputElement;
			getRequestPagination(+inputElement.value - 1);
		}
	});

	const handlerChangeGoTo = useEvent((event: React.ChangeEvent<HTMLInputElement>) => {
		const number = +event?.target?.value > 0 ? +event?.target?.value : 1;

		if (number <= totalPages) {
			dispatch(
				actionsCreator.handlerChangeSearchKeyBoardGoToListByIs({
					goTo: number,
					type: typeData,
				}),
			);
		} else {
			dispatch(
				showMessage({
					type: 'info',
					text: 'This is the last record',
				}),
			);
		}
	});

	const handlerChangeSize = useEvent((data: TFuncArgs) => {
		dispatch(
			actionsCreator.handlerChangeSearchKeyBoardSizeListByIds({
				type: typeData,
				size: data.value,
			}),
		);
		getRequestPagination(0);
	});

	const rightIconAction = useEvent(() => {
		dispatch(actionsCreator.searchTextUpdateByIds(''));
		dispatch(actionsCreator.clearResultsOverview());
	});

	return {
		onChangeInput,
		onPagePress,
		onNextPressLast,
		onPrevPressLast,
		handlerSubmit,
		leftIconAction,
		handleDownloadConfirm,
		handleClickType,
		handlerSubmitGoTo,
		onKeyPress,
		handlerChangeGoTo,
		handlerChangeSize,
		rightIconAction,
	};
};

